import { useEffect, useState } from "react";

import { Button, Callout, FormGroup, Icon, Intent } from "@blueprintjs/core";
import { Hit } from "instantsearch.js";
import uniqBy from "lodash/uniqBy";
import { Link } from "react-router";

import { pluralize } from "@/components/helpers/string-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { SetCreditsForm } from "@/components/pages_logged_in/workspace-admin/SetCreditsForm";
import { AlgoliaMultiselect } from "@/components/pieces/algolia/AlgoliaMultiselect";
import { CollapsibleSection } from "@/components/pieces/form/CollapsibleSection";
import { EmailMultiselect } from "@/components/pieces/form/EmailMultiselect";
import { SchedulingForm } from "@/components/pieces/form/SchedulingForm";
import { Avatar } from "@/components/pieces/users/Avatar";
import { GroupSelect } from "@/components/pieces/workspace_groups/GroupSelect";
import {
  LIGHT_STEEL_GRAY,
  MIDTEXT_COLOR,
  SUBTEXT_COLOR,
} from "@/css/constants";
import {
  CreateProgramUsersMutation,
  InviteClientsMutation,
  InviteClientsMutationVariables,
  UpdateWorkspaceInvitationScheduleMutationVariables,
  useValidateInviteEmailsLazyQuery,
} from "@/graphql";
import { WorkspaceInvitationBatch } from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";
import { formatCredits } from "@/utils/string";

type InviteClientsResult = NonNullable<InviteClientsMutation["inviteClients"]>;
type CreateProgramUsersResult = NonNullable<
  CreateProgramUsersMutation["createProgramUsers"]
>;

type InviteResult = InviteClientsResult | CreateProgramUsersResult;

interface InviteFormUIProps {
  result?: InviteResult | null;
  programId?: string;
  inline?: boolean;
  allowSetCredits?: boolean;
  allowSchedule?: boolean;
  workspaceCredit?: string | null;
  isEdit?: boolean;
  workspaceInvitationBatch?: WorkspaceInvitationBatch | null;
  onCancel?: () => void;
  onUserInvite?: (inviteData: {
    variables: InviteClientsMutationVariables;
  }) => void;
  onInvitationScheduleUpdate?: (inviteData: {
    variables: UpdateWorkspaceInvitationScheduleMutationVariables;
  }) => void;
  isLoading?: boolean;
  buttonText?: string;
  scheduleText?: string;
  scheduleButtonText?: string;
  allowSetGroup?: boolean;
  allowAssignCoaches?: boolean;
  planSeats?: number | null;
  occupiedSeats?: number | null;
  totalPendingInvitations?: number | null;
}

export function InviteFormUI({
  result,
  programId,
  inline = false,
  allowSetCredits = false,
  allowSchedule = true,
  workspaceCredit,
  isEdit = false,
  workspaceInvitationBatch,
  onCancel,
  onUserInvite,
  onInvitationScheduleUpdate,
  isLoading = false,
  buttonText = "Send Invitations",
  scheduleButtonText,
  scheduleText,
  allowSetGroup = true,
  allowAssignCoaches = true,
  planSeats,
  occupiedSeats,
  totalPendingInvitations,
}: InviteFormUIProps) {
  const [emailInput, setEmailInput] = useState("");
  const [coaches, setCoaches] = useState<Hit[]>([]);
  const [emailUsers, setEmailUsers] = useState<Hit[]>([]);
  const creditsText = formatCredits(workspaceCredit);
  const [amount, setAmount] = useState("0.00");
  const numberOfEmails = emailUsers?.length || 0;
  const [isSchedule, setIsSchedule] = useState(false);
  const [scheduleTime, setScheduleTime] = useState<string | null>(null);
  const [numBatchEmails, setNumBatchEmails] = useState(0);

  const [canBeInvitedEmails, setCanBeInvitedEmails] = useState<string[]>([]);
  const [existedEmails, setExistedEmails] = useState<string[]>([]);
  const [pendingInvEmails, setPendingInvEmails] = useState<string[]>([]);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [existedProgramUserEmails, setExistedProgramUserEmails] = useState<
    string[]
  >([]);
  const [groupId, setGroupId] = useState("");

  // ValidateInviteEmailsDocument
  const [validateEmails] = useValidateInviteEmailsLazyQuery({
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      const results = data?.validateInviteEmails;
      setCanBeInvitedEmails(ensureNonNullArray(results?.canBeInvitedEmails));
      setExistedEmails(ensureNonNullArray(results?.existedEmails));
      setPendingInvEmails(ensureNonNullArray(results?.pendingInvEmails));
      setInvalidEmails(ensureNonNullArray(results?.invalidEmails));
      setExistedProgramUserEmails(
        ensureNonNullArray(results?.existedProgramUserEmails),
      );
    },
  });

  const standardEmails = [
    ...new Set(
      emailUsers
        .map((x) => (x?.email && x?.email.trim()) || "")
        .filter((x) => x),
    ),
  ];

  useEffect(() => {
    if (isEdit && workspaceInvitationBatch) {
      setCoaches(ensureNonNullArray(workspaceInvitationBatch?.coaches));
      setGroupId(workspaceInvitationBatch?.group?.id || "");
      setEmailUsers(
        ensureNonNullArray(workspaceInvitationBatch?.workspaceInvitations).map(
          (invitation) => ({
            id: `custom-${Date.now()}-${invitation.email}`,
            objectID: `custom-${Date.now()}-${invitation.email}`,
            email: invitation.email,
            __position: 0,
          }),
        ),
      );
      setAmount(workspaceInvitationBatch?.credit || "0.00");
      setIsSchedule(
        workspaceInvitationBatch?.schedulingDetail?.isSchedule || false,
      );
      setScheduleTime(
        workspaceInvitationBatch?.schedulingDetail?.scheduleAt || null,
      );
      setNumBatchEmails(
        ensureNonNullArray(workspaceInvitationBatch?.workspaceInvitations)
          .length,
      );
    }
  }, [isEdit, workspaceInvitationBatch]);

  useEffect(() => {
    if (result?.ok) {
      setEmailUsers([]);
      setCoaches([]);
      setGroupId("");
      setAmount("0.00");
    }
  }, [result]);

  const onValidateEmails = (value: Hit[]) => {
    const inputEmails = [
      ...new Set(value.map((x) => x?.email.trim() || "").filter((x) => x)),
    ].join(",");
    validateEmails({ variables: { emails: inputEmails, programId } });
  };

  const handleInviteClients = () => {
    const coachIds = coaches.map((c) => c.id);
    const credit = amount;
    const ongoingInputEmails = [
      ...new Set(
        emailInput
          .replaceAll("\n", ",")
          .replaceAll(" ", ",")
          .split(",")
          .map((x) => (x && x.trim()) || "")
          .filter((x) => x),
      ),
    ].join(",");
    if (!onUserInvite) return;

    onUserInvite({
      variables: {
        emails: [standardEmails, ongoingInputEmails].join(","),
        coachIds,
        groupId,
        credit,
        isSchedule: isSchedule,
        scheduleTime,
      },
    });
  };

  const handleUpdateInviteClients = () => {
    if (!onInvitationScheduleUpdate) return;

    const coachIds = coaches.map((c) => c.id);
    const credit = amount;

    onInvitationScheduleUpdate({
      variables: {
        scheduledJobId:
          workspaceInvitationBatch?.schedulingDetail?.scheduledJobId || "",
        emails: standardEmails.join(","),
        coachIds,
        groupId,
        credit,
        isSchedule: isSchedule,
        scheduleTime,
      },
    });
  };

  const availableSeats = Math.max(
    0,
    (planSeats || 0) - (occupiedSeats || 0) - (totalPendingInvitations || 0),
  );
  const notEnoughSeats =
    canBeInvitedEmails?.length > availableSeats + numBatchEmails;
  const buttonDisabled = !emailUsers || isLoading || notEnoughSeats;

  return (
    <Flex p={inline ? "0 16px" : 3} flexDirection="column" style={{ gap: 12 }}>
      {availableSeats !== null && planSeats !== null && (
        <Flex
          p={12}
          backgroundColor={LIGHT_STEEL_GRAY}
          className="round-corners"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <Flex alignItems="center">
            <Icon icon="people" size={16} color={MIDTEXT_COLOR} />
            <Box ml={2}>
              {pluralize({
                amount: availableSeats,
                singular: "seat",
                plural: "seats",
              })}{" "}
              available
            </Box>
          </Flex>
          <Link to="/workspace/billing">Buy more seats</Link>
        </Flex>
      )}
      <Flex flexDirection="column" style={{ gap: 8 }}>
        <Box fontWeight={600} p={1}>
          Enter Emails
        </Box>
        <EmailMultiselect
          programId={programId}
          selections={emailUsers}
          onChange={(value: Hit[]) => {
            setEmailUsers(uniqBy(value, "email"));
            onValidateEmails(value);
          }}
          onInputChange={(value) => setEmailInput(value)}
          canBeInvitedEmails={canBeInvitedEmails}
          existedEmails={existedEmails}
          pendingInvEmails={pendingInvEmails}
          invalidEmails={invalidEmails}
          existedProgramUserEmails={existedProgramUserEmails}
        />
      </Flex>
      {notEnoughSeats && (
        <Box mt={3}>
          <Callout intent={Intent.WARNING} title="Not enough seats">
            <Box fontSize={14}>
              You have{" "}
              {pluralize({
                amount: availableSeats,
                singular: "seat",
                plural: "seats",
              })}{" "}
              available, but you are trying to invite{" "}
              {pluralize({
                amount: canBeInvitedEmails?.length,
                singular: "new user",
                plural: "new users",
              })}{" "}
              into the workspace.
            </Box>
          </Callout>
        </Box>
      )}

      {allowSchedule && (
        <Box pt={3}>
          <SchedulingForm
            description="Schedule time to send invitations and grant credits"
            isSchedule={isSchedule}
            scheduleTime={scheduleTime}
            onChangeScheduleTime={setScheduleTime}
            onToggleSchedule={setIsSchedule}
          />
        </Box>
      )}
      {scheduleText && (
        <FormGroup
          label={<b>Scheduling</b>}
          subLabel={
            <Box color={SUBTEXT_COLOR} fontSize={16}>
              {scheduleText}
            </Box>
          }
        />
      )}

      {allowSetGroup && (
        <CollapsibleSection title="Add to Group">
          <GroupSelect groupId={groupId} onGroupIdChange={setGroupId} />
        </CollapsibleSection>
      )}

      {allowSetCredits && (
        <CollapsibleSection title="Grant Credits">
          <SetCreditsForm
            workspaceCredit={workspaceCredit}
            numberOfEmails={numberOfEmails}
            creditsText={creditsText}
            amount={amount}
            setAmount={setAmount}
          />
        </CollapsibleSection>
      )}

      {allowAssignCoaches ? (
        <CollapsibleSection title="Assign Coaches">
          <AlgoliaMultiselect
            onChange={(selections) => setCoaches(selections)}
            filters="is_agent:true"
            placeholder="Search"
            indexName="user_index"
            selections={coaches}
            getSuggestionValue={(suggestion) => suggestion.name}
            renderSuggestion={(suggestion) => {
              const user = {
                name: suggestion.name,
                photo: suggestion.photo,
              };
              return (
                <Flex alignItems="center">
                  <Box>
                    <Avatar user={user} />
                  </Box>
                  <Box ml={3}>
                    <Box>{suggestion.name}</Box>
                  </Box>
                </Flex>
              );
            }}
          />
        </CollapsibleSection>
      ) : null}

      <FormGroup style={{ marginTop: 16, marginBottom: 0 }}>
        {isEdit ? (
          <Flex justifyContent="space-between">
            <Button
              large={inline}
              intent="primary"
              disabled={buttonDisabled}
              onClick={handleUpdateInviteClients}
            >
              {isSchedule ? "Save" : "Invite Now"}
            </Button>
            <Button
              large={inline}
              intent="danger"
              disabled={buttonDisabled}
              onClick={onCancel}
            >
              Cancel Invitation
            </Button>
          </Flex>
        ) : (
          <Flex>
            <Button
              large={inline}
              intent="primary"
              disabled={buttonDisabled}
              onClick={handleInviteClients}
            >
              {isLoading
                ? "Sending..."
                : isSchedule
                  ? scheduleButtonText
                  : buttonText}
            </Button>
          </Flex>
        )}
      </FormGroup>
    </Flex>
  );
}
